import React, { useState } from "react";
import { Phone, PhoneCall, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { updateMe } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { useI18n } from "@/i18n/I18nContext";

// Contacto de emergencia que elige cada persona (nombre y teléfono). Se usa
// en Perfil → Ajustes y en Ayuda → «Seguridad en un viaje», donde se llama
// con un toque. variant="row": fila de «Mis datos» del perfil; si no, el
// bloque de la ayuda (botón grande de llamar).
export default function EmergencyContact({ variant = "block" }) {
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const saved = user?.emergency_phone;

  const startEdit = () => {
    setName(user?.emergency_name || "");
    setPhone(user?.emergency_phone || "");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateMe({ emergency_name: name, emergency_phone: phone });
      setUser((u) => ({ ...u, ...(updated || {}), emergency_name: name.trim() || null, emergency_phone: phone.trim() || null }));
      setEditing(false);
      toast.success(t("emergency.saved"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("emergency.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const tel = saved ? `tel:${saved.replace(/[^\d+]/g, "")}` : "";
  const form = (
    <form onSubmit={save} className="w-full grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid="emergency-form">
      <label className="text-sm font-semibold text-slate-700">
        {t("emergency.name")}
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="off"
          data-testid="emergency-name"
          className="mt-1 w-full h-11 rounded-xl border border-slate-200 px-3 text-base font-normal focus:border-colygon focus:ring-2 focus:ring-colygon-light/50 outline-none" />
      </label>
      <label className="text-sm font-semibold text-slate-700">
        {t("emergency.phone")}
        <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" inputMode="tel" maxLength={24}
          autoComplete="off" placeholder="+34 600 000 000" data-testid="emergency-phone"
          className="mt-1 w-full h-11 rounded-xl border border-slate-200 px-3 text-base font-normal focus:border-colygon focus:ring-2 focus:ring-colygon-light/50 outline-none" />
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="emergency-save"
          className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-xl bg-colygon-dark text-white text-sm font-bold hover:bg-colygon-darkest disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} {t("emergency.save")}
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={saving}
          className="h-11 px-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          {t("emergency.cancel")}
        </button>
      </div>
    </form>
  );

  if (variant === "row") {
    if (editing) return form;
    const smallBtn = "shrink-0 inline-flex items-center gap-1.5 border border-colygon-light text-colygon-dark hover:bg-colygon-light/20 transition-all text-sm px-3 py-1.5 rounded-xl font-bold";
    return saved ? (
      <>
        <div className="min-w-0">
          {user?.emergency_name && <div className="font-medium text-slate-900 truncate">{user.emergency_name}</div>}
          <a href={tel} data-testid="emergency-call" className="text-colygon-dark font-semibold tabular-nums hover:underline">{saved}</a>
        </div>
        <button type="button" onClick={startEdit} data-testid="emergency-edit" className={smallBtn}>{t("profile.change")}</button>
      </>
    ) : (
      <>
        <span className="text-slate-500">{t("profile.not_set")}</span>
        <button type="button" onClick={startEdit} data-testid="emergency-add" className={smallBtn}>{t("profile.configure")}</button>
      </>
    );
  }

  const body = editing ? form : saved ? (
    <div className="flex flex-wrap items-center gap-2">
      <a href={tel} data-testid="emergency-call"
        className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-colygon-dark text-white text-sm font-bold hover:bg-colygon-darkest">
        <PhoneCall className="h-4 w-4" aria-hidden="true" />
        {t("emergency.call", { name: user?.emergency_name || saved })}
      </a>
      <span className="text-sm text-slate-600 tabular-nums">{saved}</span>
      <button type="button" onClick={startEdit} data-testid="emergency-edit"
        className="inline-flex items-center gap-1 text-sm font-semibold text-colygon-dark hover:underline">
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> {t("emergency.edit")}
      </button>
    </div>
  ) : (
    <button type="button" onClick={startEdit} data-testid="emergency-add"
      className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 border-dashed border-colygon-light text-sm font-bold text-colygon-darkest hover:bg-colygon-light/10">
      <Phone className="h-4 w-4" aria-hidden="true" /> {t("emergency.add")}
    </button>
  );

  return <div data-testid="emergency-contact">{body}</div>;
}
